/**
 * « Chapitres à compléter » : les défauts d'un chapitre importé, en logique
 * pure (testée), partagée par la Couverture et la liste des Chapitres.
 *
 * Tout est lu sur des comptes rendus par le serveur (structure du manifeste,
 * objectifs, faits) : aucune règle de contenu, aucun mot recherché dans le
 * texte. Un compte absent (serveur pas à jour) = défaut non évalué.
 */
import type { OssatureChapterItem } from "../../api/coverage/coverage";
import type { PillTone } from "../ui/StatusPill";

/** Du plus grave au moins grave. */
export const CHAPTER_ISSUES = ["no_objectives", "lessons_empty", "lessons_weak", "no_facts", "inactive"] as const;
export type ChapterIssue = (typeof CHAPTER_ISSUES)[number];

/** Ce qu'il faut d'un chapitre pour évaluer ses défauts. */
export type IssueFields = Pick<
  OssatureChapterItem,
  | "document_id"
  | "is_active"
  | "objectives"
  | "objectives_with_facts"
  | "lessons"
  | "lessons_ready"
  | "lessons_weak"
  | "lessons_empty"
>;

const plural = (n: number, one: string, many: string) => (n > 1 ? many : one);

/**
 * Défauts d'un chapitre, du plus grave au moins grave. Un chapitre pas encore
 * importé n'en a pas (il est « à importer », c'est un autre état). Sans aucun
 * objectif, les défauts qui en découlent (leçons, faits) ne sont pas répétés :
 * il n'y a qu'une chose à faire, extraire. Sans aucun fait, toutes les leçons
 * qui ont des objectifs sont faibles : « sans faits » le dit seul.
 */
export const chapterIssues = (c: IssueFields): ChapterIssue[] => {
  if (!c.document_id) return [];
  const out: ChapterIssue[] = [];
  if (c.objectives === 0) {
    out.push("no_objectives");
  } else {
    const noFacts = c.objectives_with_facts === 0;
    if ((c.lessons_empty ?? 0) > 0) out.push("lessons_empty");
    if (!noFacts && (c.lessons_weak ?? 0) > 0) out.push("lessons_weak");
    if (noFacts) out.push("no_facts");
  }
  if (c.is_active === false) out.push("inactive");
  return out;
};

/** Au moins un défaut. */
export const isIncomplete = (c: IssueFields): boolean => chapterIssues(c).length > 0;

export const issueTone = (issue: ChapterIssue): PillTone =>
  issue === "no_objectives" || issue === "lessons_empty" ? "error" : issue === "inactive" ? "neutral" : "warning";

/** Libellé court d'un défaut, avec son compte quand il en a un. */
export const issueLabel = (issue: ChapterIssue, c: IssueFields): string => {
  switch (issue) {
    case "no_objectives":
      return "Sans objectifs";
    case "lessons_empty": {
      const n = c.lessons_empty ?? 0;
      return `${n} ${plural(n, "leçon vide", "leçons vides")}`;
    }
    case "lessons_weak": {
      const n = c.lessons_weak ?? 0;
      return `${n} ${plural(n, "leçon faible", "leçons faibles")}`;
    }
    case "no_facts":
      return "Sans faits";
    case "inactive":
      return "Inactif";
  }
};

/** Phrase d'aide (infobulle) : ce que le défaut veut dire, et quoi faire. */
export const issueHint = (issue: ChapterIssue): string => {
  switch (issue) {
    case "no_objectives":
      return "Aucun objectif extrait : l'extraction n'a rien donné ou n'a pas tourné. Le chapitre n'est pas jouable.";
    case "lessons_empty":
      return "Aucun objectif n'a été déduit de ces leçons : l'élève ne les travaillera pas. Une ré-extraction peut les combler ; le détail est dans l'onglet « Leçons » du chapitre.";
    case "lessons_weak":
      return "Moins de 2 objectifs, ou des objectifs sans base de faits : voir l'onglet « Leçons » du chapitre.";
    case "no_facts":
      return "Des objectifs, mais aucun n'a de base de faits : le chapitre n'est pas jouable.";
    case "inactive":
      return "Masqué aux élèves.";
  }
};

export interface IssueBadge {
  issue: ChapterIssue;
  label: string;
  hint: string;
  tone: PillTone;
}

export const describeIssues = (c: IssueFields): IssueBadge[] =>
  chapterIssues(c).map((issue) => ({ issue, label: issueLabel(issue, c), hint: issueHint(issue), tone: issueTone(issue) }));

/* ---------- Filtre ---------- */

/**
 * `all` = pas de filtre, `any` = au moins un défaut, sinon un défaut précis.
 * Une valeur d'URL inconnue (dont les anciennes `lesson_gap` et
 * `official_missing`) vaut « tous ».
 */
export type IssueFilter = "all" | "any" | ChapterIssue;

export const ISSUE_FILTER_LABELS: Record<IssueFilter, string> = {
  all: "Tous",
  any: "À compléter",
  no_objectives: "Sans objectifs",
  lessons_empty: "Leçons vides",
  lessons_weak: "Leçons faibles",
  no_facts: "Sans faits",
  inactive: "Inactifs",
};

export const asIssueFilter = (raw: string | null | undefined): IssueFilter =>
  raw === "any" || (CHAPTER_ISSUES as readonly string[]).includes(raw ?? "") ? (raw as IssueFilter) : "all";

export const matchesIssue = (issues: ChapterIssue[], filter: IssueFilter): boolean =>
  filter === "all" ? true : filter === "any" ? issues.length > 0 : issues.includes(filter);

/* ---------- Comptes ---------- */

export interface IssueTotals {
  /** Chapitres importés évalués. */
  imported: number;
  /** Chapitres importés avec au moins un défaut. */
  incomplete: number;
  /** Chapitres concernés par chaque défaut. */
  byIssue: Record<ChapterIssue, number>;
  /** Les comptes par leçon sont-ils rendus par le serveur ? */
  measured: { lessons: boolean };
}

export const issueTotals = (chapters: IssueFields[]): IssueTotals => {
  const byIssue = Object.fromEntries(CHAPTER_ISSUES.map((i) => [i, 0])) as Record<ChapterIssue, number>;
  let imported = 0;
  let incomplete = 0;
  let lessons = false;
  chapters.forEach((c) => {
    if (!c.document_id) return;
    imported += 1;
    if (c.lessons_empty != null) lessons = true;
    const issues = chapterIssues(c);
    if (issues.length) incomplete += 1;
    issues.forEach((i) => (byIssue[i] += 1));
  });
  return { imported, incomplete, byIssue, measured: { lessons } };
};

const SUMMARY: Record<ChapterIssue, (n: number) => string> = {
  no_objectives: (n) => `${n} sans objectifs`,
  lessons_empty: (n) => `${n} avec une leçon vide`,
  lessons_weak: (n) => `${n} avec une leçon faible`,
  no_facts: (n) => `${n} sans faits`,
  inactive: (n) => `${n} ${plural(n, "inactif", "inactifs")}`,
};

/**
 * Détail d'un total, pour le sous-texte du chiffre clé :
 * « 6 avec une leçon vide · 19 avec une leçon faible ».
 * Un chapitre peut compter dans plusieurs parts.
 */
export const issueSummary = (t: IssueTotals): string => {
  if (t.imported === 0) return "aucun chapitre importé";
  const parts = CHAPTER_ISSUES.filter((i) => t.byIssue[i] > 0).map((i) => SUMMARY[i](t.byIssue[i]));
  const text = parts.length ? parts.join(" · ") : "tous les chapitres importés sont complets";
  return t.measured.lessons ? text : `${text} (leçons pas encore mesurées)`;
};

/** « 9/12 leçons prêtes » ; null quand les leçons ne sont pas comptées. */
export const formatLessonsReady = (c: { lessons?: number; lessons_ready?: number }): string | null =>
  c.lessons ? `${c.lessons_ready ?? 0}/${c.lessons} ${plural(c.lessons, "leçon prête", "leçons prêtes")}` : null;

/* ---------- Matrice ---------- */

export interface CellIssues {
  /** Chapitres de la case avec au moins un défaut. */
  count: number;
  /** Au moins un défaut grave (sans objectifs, leçon vide). */
  severe: boolean;
}

/** Chapitres à compléter par case matière × niveau (clé `subject|grade`, cf. `cellKey`). */
export const issuesByCell = (
  chapters: (IssueFields & Pick<OssatureChapterItem, "subject_id" | "grade_id">)[],
): Map<string, CellIssues> => {
  const out = new Map<string, CellIssues>();
  chapters.forEach((c) => {
    const issues = chapterIssues(c);
    if (!issues.length) return;
    const key = `${c.subject_id}|${c.grade_id}`;
    const prev = out.get(key) ?? { count: 0, severe: false };
    out.set(key, {
      count: prev.count + 1,
      severe: prev.severe || issues.some((i) => issueTone(i) === "error"),
    });
  });
  return out;
};

/* ---------- Liste des chapitres ---------- */

/** Complétude des chapitres d'un programme, par `document_id`. */
export const completenessByDocument = (chapters: OssatureChapterItem[]): Map<string, OssatureChapterItem> => {
  const out = new Map<string, OssatureChapterItem>();
  chapters.forEach((c) => c.document_id && out.set(c.document_id, c));
  return out;
};

/**
 * Défauts d'une ligne de la liste : la complétude du serveur, avec l'état
 * actif de la ligne (qui suit une modification faite dans le panneau). null =
 * inconnus (complétude non chargée, ou document absent de l'ossature).
 */
export const documentIssues = (
  doc: { id: string; is_active?: boolean | null },
  item: OssatureChapterItem | undefined,
): ChapterIssue[] | null =>
  item ? chapterIssues({ ...item, document_id: doc.id, is_active: doc.is_active ?? item.is_active }) : null;
